/*
 * MAW audio facade: buses, SFX playback, loops and music (docs/07 §4).
 *
 * Graph (all inside Howler's shared AudioContext):
 *   music engine ─► musicBus (≤ 0.35) ─────────────────────────┐
 *   one-shots/loops ─► sfxBus (≤ 0.5) ─► limiter ─────────────┼─► Howler.masterGain ─► destination
 *   typing blips ─► blipBus ─► sfxBus                           │   (Howler.volume = master)
 *
 * Nothing here touches the AudioContext until unlock() is called from a user gesture.
 * Every entry point is guarded: in Node / without Web Audio all calls are silent no-ops.
 */
import { Howler } from 'howler';
import { SFX, buildSfxSamples, createLoop, type LoopHandle, type LoopName, type ZzfxApi } from './sfx';
import { createMusic, type MusicMode } from './music';

export type SfxName =
  | 'leverGrab' | 'ratchet' | 'clack' | 'perfect'
  | 'reelStop' | 'fate' | 'anticipation'
  | 'patternBlip' | 'countTick' | 'kachunk' | 'coin'
  | 'bigHit' | 'swell' | 'laugh' | 'jingle' | 'gorge'
  | 'nearMiss' | 'crunch' | 'sparkle' | 'pop' | 'chomp' | 'stamp'
  | 'printer' | 'register' | 'rumble' | 'choir'
  | 'blip' | 'click' | 'hover' | 'error'
  | 'scratch' | 'scratchWin' | 'scratchVoid' | 'bonusStart' | 'wake'
  | 'cardFlip' | 'wheelTick' | 'devour' | 'static' | 'phone' | 'relic' | 'sign'
  | 'rain' | 'door' | 'organ';

export interface PlayOpts {
  pitch?: number /* semitones offset */;
  volume?: number /* 0..1 multiplier */;
}

/** Bus ceilings: music + sfx at full volume stay ≤ ~0.85 into Howler's master. */
const MUSIC_BUS_MAX = 0.35;
const SFX_BUS_MAX = 0.5;

interface Graph {
  ctx: AudioContext;
  music: GainNode;
  sfx: GainNode;
  blip: GainNode;
}

interface Voice {
  src: AudioBufferSourceNode;
  end: number;
}

let graph: Graph | null = null;
let zz: ZzfxApi | null = null;
let zzLoading = false;
const volumes = { master: 1, music: 1, sfx: 1 };
const buffers = new Map<string, AudioBuffer>();
const rrIndex = new Map<SfxName, number>();
const lastPlay = new Map<SfxName, number>();
const voices = new Map<SfxName, Voice[]>();
const loops = new Map<LoopName, LoopHandle>();
const music = createMusic();

const clamp01 = (x: number): number => (Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0);

function hasWebAudio(): boolean {
  return typeof window !== 'undefined' && (typeof AudioContext !== 'undefined' || 'webkitAudioContext' in window);
}

function applyVolumes(): void {
  const g = graph;
  if (!g) return;
  try {
    Howler.volume(clamp01(volumes.master));
    const now = g.ctx.currentTime;
    g.music.gain.setTargetAtTime(MUSIC_BUS_MAX * clamp01(volumes.music), now, 0.05);
    g.sfx.gain.setTargetAtTime(SFX_BUS_MAX * clamp01(volumes.sfx), now, 0.05);
  } catch {
    /* ignore */
  }
}

function buildGraph(ctx: AudioContext, root: AudioNode): Graph {
  const music = ctx.createGain();
  music.gain.value = MUSIC_BUS_MAX * clamp01(volumes.music);
  music.connect(root);

  // Gentle safety limiter on SFX only (so music isn't pumped by it).
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -8;
  limiter.knee.value = 6;
  limiter.ratio.value = 12;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.15;
  limiter.connect(root);

  const sfx = ctx.createGain();
  sfx.gain.value = SFX_BUS_MAX * clamp01(volumes.sfx);
  sfx.connect(limiter);

  const blip = ctx.createGain();
  blip.gain.value = 1;
  blip.connect(sfx);
  return { ctx, music, sfx, blip };
}

function loadZzfx(): void {
  if (zz || zzLoading) return;
  zzLoading = true;
  // Dynamic import: zzfx creates its own AudioContext at module evaluation, so it must not be
  // loaded in Node or before a gesture. We only use its sample builder and close its context.
  import('zzfx')
    .then(({ ZZFX }) => {
      ZZFX.volume = 1; // levels are handled by SfxDef.gain and the buses
      try {
        void ZZFX.audioContext.close().catch(() => undefined);
      } catch {
        /* ignore */
      }
      zz = ZZFX;
      warmUp();
    })
    .catch(() => {
      zzLoading = false;
    });
}

/** Pre-build the first variant of every sound, one per frame-ish, so first plays are instant. */
function warmUp(): void {
  const names = Object.keys(SFX) as SfxName[];
  let i = 0;
  const next = (): void => {
    const name = names[i++];
    if (!name) return;
    try {
      getBuffer(name, 0);
    } catch {
      /* ignore */
    }
    setTimeout(next, 16);
  };
  setTimeout(next, 50);
}

function getBuffer(name: SfxName, variant: number): AudioBuffer | null {
  const g = graph;
  const z = zz;
  if (!g || !z) return null;
  const key = `${name}#${variant}`;
  let buf = buffers.get(key);
  if (!buf) {
    const samples = buildSfxSamples(SFX[name], variant, z);
    buf = g.ctx.createBuffer(1, samples.length, z.sampleRate);
    buf.copyToChannel(samples, 0);
    buffers.set(key, buf);
  }
  return buf;
}

function play(name: SfxName, opts?: PlayOpts): void {
  const g = graph;
  if (!g || !zz) return;
  try {
    const ctx = g.ctx;
    if (ctx.state !== 'running') return; // don't queue sounds that would all fire on resume
    const def = SFX[name];
    if (!def) return;
    const now = ctx.currentTime;

    const minGap = def.minGap ?? 0;
    const last = lastPlay.get(name);
    if (minGap > 0 && last !== undefined && now - last < minGap) return;
    lastPlay.set(name, now);

    const nVariants = Math.max(1, def.variants ?? 3);
    const rr = ((rrIndex.get(name) ?? -1) + 1) % nVariants;
    rrIndex.set(name, rr);
    const buf = getBuffer(name, rr);
    if (!buf) return;

    // Voice limiting: drop finished voices, cut the oldest if over the cap.
    const list = (voices.get(name) ?? []).filter((v) => v.end > now);
    const maxVoices = def.maxVoices ?? 4;
    while (list.length >= maxVoices) {
      const old = list.shift();
      try {
        old?.src.stop(now + 0.005);
      } catch {
        /* ignore */
      }
    }

    const jitter = def.jitter ?? 0.05;
    const semis = Number.isFinite(opts?.pitch) ? (opts?.pitch ?? 0) : 0;
    const rate = 2 ** (semis / 12) * (1 + jitter * (Math.random() * 2 - 1));
    const vol = clamp01(opts?.volume ?? 1) * def.gain;
    if (vol <= 0) return;

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const gain = ctx.createGain();
    gain.gain.value = vol;
    src.connect(gain).connect(def.bus === 'blip' ? g.blip : g.sfx);
    src.onended = () => {
      try {
        gain.disconnect();
      } catch {
        /* ignore */
      }
    };
    src.start(now);
    list.push({ src, end: now + buf.duration / rate });
    voices.set(name, list);
  } catch {
    /* audio must never break the game */
  }
}

export const audio = {
  /** Idempotent. Call on first user gesture; resumes the AudioContext. */
  unlock(): void {
    if (!hasWebAudio()) return;
    try {
      if (!Howler.ctx) Howler.volume(Howler.volume()); // forces Howler to create ctx + masterGain
      const ctx = Howler.ctx;
      const root = Howler.masterGain;
      if (!ctx || !root) return;
      // Howler would otherwise suspend the context after 30 s without Howl playback.
      Howler.autoSuspend = false;
      if (!graph) {
        graph = buildGraph(ctx, root);
        applyVolumes();
        music.attach(ctx, graph.music);
      }
      if (ctx.state !== 'running') void ctx.resume().catch(() => undefined);
      loadZzfx();
    } catch {
      /* ignore */
    }
  },

  setVolumes(v: { master: number; music: number; sfx: number }): void {
    volumes.master = clamp01(v.master);
    volumes.music = clamp01(v.music);
    volumes.sfx = clamp01(v.sfx);
    applyVolumes();
  },

  play,

  /** Looping sounds (reel whirr while spinning, scratch scrape while scratching, tension drone). */
  startLoop(name: LoopName): void {
    const g = graph;
    if (!g || loops.has(name)) return;
    try {
      loops.set(name, createLoop(name, g.ctx, g.sfx));
    } catch {
      /* ignore */
    }
  },

  stopLoop(name: LoopName): void {
    const h = loops.get(name);
    if (!h) return;
    loops.delete(name);
    try {
      h.stop();
    } catch {
      /* ignore */
    }
  },

  music: {
    /** Start the placeholder jazz loop (idempotent). If called before unlock(), it starts on unlock. */
    start(): void {
      music.start();
    },
    stop(): void {
      music.stop();
    },
    /** 0..1: wow/flutter, lowpass, saturation, reverb, detuned piano (>0.6), drums out + drone (>0.7). */
    setDecay(d: number): void {
      music.setDecay(d);
    },
    /** Temporarily lower the music by `db` (negative) for `ms`, then recover. */
    duck(db: number, ms: number): void {
      music.duck(db, ms);
    },
    /** Act layers: 0 dry … 3 organ and choir (docs/07 §4.1). */
    setAct(act: number): void {
      music.setAct(act);
    },
    /** A bonus game's loop (null = back to the band). */
    setMode(mode: MusicMode | null): void {
      music.setMode(mode);
    },
    /** Pitch-down stop (Consumed sequence); music.start() restarts it. */
    tapeStop(): void {
      music.tapeStop();
    },
  },
};
